import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from "react";
import {
  subscribeToQueue, subscribeToNowPlaying, subscribeToVibeScore,
  setNowPlaying, updatePlayback, removeSong,
} from "../services/queueService";
import {
  getVenue, checkInToVenue, checkOutOfVenue,
  subscribeToActiveUsers, PRESENCE_REFRESH_MS,
} from "../services/venueService";
import {
  initializePlayer, playSong, pausePlayback,
  getValidSpotifyToken, activatePlayer, restartTrack,
  parsePlaylistId, getPlaylistInfo, getPlaylistTracks, fadeVolume, PLAYER_VOLUME,
} from "../services/spotifyService";
import { useAuth } from "./AuthContext";

const VenueContext = createContext(null);

const FALLBACK_KEY = "tapped_fallback_playlist";
const FADE_OUT_MS = 1500;
const FADE_IN_MS = 1500;

export const SKIP_VOTES_NEEDED = 3;

function durationToMs(duration) {
  const m = /^(\d+):(\d{2})$/.exec(duration || "");
  return m ? (parseInt(m[1], 10) * 60 + parseInt(m[2], 10)) * 1000 : 0;
}

export function VenueProvider({ children }) {
  const { user, profile } = useAuth();
  const [venueId, setVenueId] = useState(null);
  const [venue, setVenue] = useState(null);
  const [queue, setQueue] = useState([]);
  const [nowPlaying, setNowPlayingState] = useState(null);
  // Last playback report from the venue's player, and when we received it
  const [playback, setPlayback] = useState({ isPlaying: false, positionMs: 0, durationMs: 0, receivedAt: 0 });
  const [positionMs, setPositionMs] = useState(0);
  const [skipVoters, setSkipVoters] = useState([]);
  const [vibeScore, setVibeScore] = useState({ good: 0, bad: 0 });
  const [activeUsers, setActiveUsers] = useState([]);
  const [isVenueMode, setIsVenueMode] = useState(false);
  const [playerReady, setPlayerReady] = useState(false);
  const [playerError, setPlayerError] = useState(null);
  const [deviceId, setDeviceId] = useState(null);
  const [player, setPlayer] = useState(null);
  // The venue's own playlist, played whenever nobody has requested anything
  const [fallback, setFallback] = useState(null);
  const activeRef = useRef(null);
  const deviceIdRef = useRef(null);

  // Latest values for callbacks that are registered once (the Spotify player)
  const venueIdRef = useRef(null);
  const selfUidRef = useRef(null);
  const nowPlayingRef = useRef(null);
  const queueRef = useRef([]);
  const playbackRef = useRef(playback);
  const endedTrackRef = useRef(null);
  const advancingRef = useRef(false);
  const activatedRef = useRef(false);
  const retryBlockedUntilRef = useRef(0);
  const lastWriteRef = useRef({ at: 0, positionMs: 0, isPlaying: null });
  const fallbackTracksRef = useRef([]);
  const fallbackOrderRef = useRef([]);
  const lastFallbackIdRef = useRef(null);
  const transitioningRef = useRef(false);

  useEffect(() => { deviceIdRef.current = deviceId; }, [deviceId]);
  useEffect(() => { venueIdRef.current = venueId; }, [venueId]);
  useEffect(() => { selfUidRef.current = user?.uid || null; }, [user]);
  useEffect(() => { nowPlayingRef.current = nowPlaying; }, [nowPlaying]);
  useEffect(() => { queueRef.current = queue; }, [queue]);
  useEffect(() => { playbackRef.current = playback; }, [playback]);
  const currentPositionMs = useCallback(() => {
    const pb = playbackRef.current;
    const elapsed = pb.isPlaying ? Date.now() - pb.receivedAt : 0;
    return Math.min(pb.positionMs + elapsed, pb.durationMs || Infinity);
  }, []);

  // Subscribe to venue data
  useEffect(() => {
    if (!venueId) return;
    const unsubQueue = subscribeToQueue(venueId, setQueue);
    const unsubNP = subscribeToNowPlaying(venueId, (data) => {
      setNowPlayingState(data.song || null);
      setSkipVoters(data.skipVoters || []);
      setPlayback({
        isPlaying: !!data.isPlaying && !!data.song,
        positionMs: data.positionMs || 0,
        durationMs: data.durationMs || 0,
        receivedAt: Date.now(),
      });
    });
    const unsubVibe = subscribeToVibeScore(venueId, setVibeScore);
    const unsubUsers = subscribeToActiveUsers(venueId, setActiveUsers, () => selfUidRef.current);
    getVenue(venueId).then(setVenue);
    return () => { unsubQueue(); unsubNP(); unsubVibe(); unsubUsers(); };
  }, [venueId]);

  // Check in/out, and keep our "here right now" record fresh while the app is open
  useEffect(() => {
    if (!venueId || !user) return;
    checkInToVenue(venueId, { ...user, privacy: profile?.privacy || "public" });
    if (user.isGuest) return;

    // Rewrites the whole record (not just the timestamp) so someone who was
    // tidied away while their phone slept reappears properly
    const heartbeat = () => checkInToVenue(venueId, { ...user, privacy: profile?.privacy || "public" }).catch(() => {});
    activeRef.current = setInterval(heartbeat, PRESENCE_REFRESH_MS);
    // Coming back to the tab (or the phone waking up) counts as being here again
    const onVisible = () => { if (document.visibilityState === "visible") heartbeat(); };
    // Closing the tab: try to leave right away. Not guaranteed, which is why the
    // list also hides records that stop refreshing.
    const onPageHide = () => { checkOutOfVenue(venueId, user.uid).catch(() => {}); };
    const onPageShow = heartbeat;
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("pageshow", onPageShow);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("pageshow", onPageShow);
      checkOutOfVenue(venueId, user.uid).catch(() => {});
      clearInterval(activeRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venueId, user]);

  // Live position: the venue reports it when something changes, everyone else
  // counts forward from there so the bar moves smoothly.
  useEffect(() => {
    const compute = () => {
      const elapsed = playback.isPlaying ? Date.now() - playback.receivedAt : 0;
      const max = playback.durationMs || Infinity;
      setPositionMs(Math.min(playback.positionMs + elapsed, max));
    };
    compute();
    if (!playback.isPlaying) return;
    const t = setInterval(compute, 250);
    return () => clearInterval(t);
  }, [playback]);

  const durationMs = playback.durationMs || durationToMs(nowPlaying?.duration);
  const progress = durationMs ? Math.min(100, (positionMs / durationMs) * 100) : 0;
  const isPlaying = playback.isPlaying;

  // ─── VENUE PLAYER (host device only) ─────────────────────────────────────────

  // Start a song from the queue on this device's speakers and make it the
  // current song. It leaves the queue as soon as it starts.
  const startSong = useCallback(async (song) => {
    const vid = venueIdRef.current;
    const currentDeviceId = deviceIdRef.current;
    if (!vid || !currentDeviceId || !song) return false;

    const attempt = async (token) => playSong(token, currentDeviceId, song.uri);
    try {
      let token = await getValidSpotifyToken();
      if (!token) throw new Error("Spotify isn't connected");
      try {
        await attempt(token);
      } catch (err) {
        // Token may have just expired; get a fresh one and try once more
        token = await getValidSpotifyToken();
        if (!token) throw err;
        await attempt(token);
      }
    } catch (err) {
      console.error("❌ Could not play song:", err.message);
      setPlayerError(`Couldn't play "${song.title}": ${err.message}`);
      retryBlockedUntilRef.current = Date.now() + 15000;
      return false;
    }

    setPlayerError(null);
    activatedRef.current = true;
    endedTrackRef.current = null;
    const { docId, votes, voters, addedAt, ...songInfo } = song;
    const startMs = durationToMs(song.duration);
    lastWriteRef.current = { at: Date.now(), positionMs: 0, isPlaying: true };
    await setNowPlaying(vid, songInfo, true, 0, startMs);
    if (docId) await removeSong(vid, docId);
    return true;
  }, []);

  // Shuffled walk through the venue's playlist; reshuffles when it runs out and
  // never plays the same song twice in a row
  const nextFallbackSong = useCallback(() => {
    const tracks = fallbackTracksRef.current;
    if (!tracks.length) return null;
    if (fallbackOrderRef.current.length === 0) {
      const order = tracks.map((_, i) => i);
      for (let i = order.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [order[i], order[j]] = [order[j], order[i]];
      }
      const last = order.length - 1;
      if (order.length > 1 && tracks[order[last]].id === lastFallbackIdRef.current) {
        [order[0], order[last]] = [order[last], order[0]];
      }
      fallbackOrderRef.current = order;
    }
    const track = tracks[fallbackOrderRef.current.pop()];
    lastFallbackIdRef.current = track.id;
    return { ...track, source: "playlist" };
  }, []);

  // Play whatever is on top of the queue (requests always come first), then the
  // venue's playlist, or go idle if there is neither
  const advance = useCallback(async () => {
    if (advancingRef.current) return;
    advancingRef.current = true;
    try {
      const next = queueRef.current[0];
      if (next) {
        await startSong(next);
        return;
      }
      if (fallbackTracksRef.current.length) {
        // A song that can't be played (e.g. not available here) is skipped
        for (let i = 0; i < 5; i++) {
          const song = nextFallbackSong();
          if (song && (await startSong(song))) return;
        }
      }
      if (venueIdRef.current) {
        lastWriteRef.current = { at: Date.now(), positionMs: 0, isPlaying: false };
        await setNowPlaying(venueIdRef.current, null, false, 0, 0);
      }
    } finally {
      advancingRef.current = false;
    }
  }, [startSong, nextFallbackSong]);

  // Move to another song without a hard cut: fade the current one out, start the
  // next silently, then fade it in
  const smoothTransition = useCallback(async (action, { onlyIfQueued = false } = {}) => {
    if (transitioningRef.current || advancingRef.current) return;
    transitioningRef.current = true;
    try {
      const audible = !!nowPlayingRef.current && playbackRef.current.isPlaying;
      if (audible) await fadeVolume(0, FADE_OUT_MS);
      if (onlyIfQueued && queueRef.current.length === 0) {
        // The request was withdrawn during the fade: keep the current song going
        await fadeVolume(PLAYER_VOLUME, 600);
        return;
      }
      await action();
      if (audible) await fadeVolume(PLAYER_VOLUME, FADE_IN_MS);
    } finally {
      await fadeVolume(PLAYER_VOLUME, 100);
      transitioningRef.current = false;
    }
  }, []);

  // Called by the Spotify player on every play, pause, seek or track change
  const handlePlayerState = useCallback((state) => {
    const vid = venueIdRef.current;
    const current = nowPlayingRef.current;
    if (!state || !vid || !current) return;
    const track = state.track_window?.current_track;
    // Ignore reports about some other track (e.g. one still winding down)
    if (!track || track.uri !== current.uri) return;

    // Spotify's signal that a track finished: stopped at 0 with itself in "previous"
    const ended = state.paused && state.position === 0 &&
      state.track_window.previous_tracks.some((t) => t.id === track.id);
    if (ended) {
      if (transitioningRef.current) return;
      if (endedTrackRef.current !== track.id) {
        endedTrackRef.current = track.id;
        advance();
      }
      return;
    }

    // Skip reports that match what we already told everyone
    const last = lastWriteRef.current;
    const expected = last.isPlaying ? last.positionMs + (Date.now() - last.at) : last.positionMs;
    const isPlayingNow = !state.paused;
    if (last.isPlaying === isPlayingNow && Math.abs(expected - state.position) < 1500) return;

    lastWriteRef.current = { at: Date.now(), positionMs: state.position, isPlaying: isPlayingNow };
    updatePlayback(vid, { isPlaying: isPlayingNow, positionMs: state.position, durationMs: state.duration });
  }, [advance]);

  const stateHandlerRef = useRef(handlePlayerState);
  useEffect(() => { stateHandlerRef.current = handlePlayerState; }, [handlePlayerState]);

  // Initialize player - called from MainApp when token is available
  const initPlayer = useCallback((token) => {
    setPlayerError(null);
    initializePlayer(
      token,
      (p, id) => {
        setPlayer(p);
        setDeviceId(id);
        deviceIdRef.current = id;
        setPlayerReady(true);
        setPlayerError(null);
      },
      (err) => {
        console.error("❌ Player init error:", err);
        setPlayerError(err);
        setPlayerReady(false);
      },
      (state) => stateHandlerRef.current(state)
    );
  }, []);

  // Host pressed a song's play button in the queue
  const handlePlaySong = useCallback(async (song) => {
    await activatePlayer();
    await smoothTransition(() => startSong(song));
  }, [startSong, smoothTransition]);

  // Host pressed "Start music"
  const handleStartMusic = useCallback(async () => {
    await activatePlayer();
    activatedRef.current = true;
    await advance();
  }, [advance]);

  const handlePlayPause = useCallback(async () => {
    const vid = venueIdRef.current;
    const current = nowPlayingRef.current;
    const currentDeviceId = deviceIdRef.current;
    if (!vid || !current || !currentDeviceId) return;
    await activatePlayer();
    try {
      const state = await player?.getCurrentState?.();
      const loaded = state?.track_window?.current_track?.uri === current.uri;

      if (loaded && player) {
        // The song is still loaded in this player: pause and resume it directly,
        // which keeps the exact position
        if (state.paused) await player.resume();
        else await player.pause();
        return;
      }

      // This device has nothing loaded (e.g. after a page reload). Start the song
      // again from where it was paused rather than from the beginning.
      const token = await getValidSpotifyToken();
      if (!token) return;
      if (isPlaying) {
        await pausePlayback(token, currentDeviceId);
      } else {
        const resumeAt = currentPositionMs();
        await playSong(token, currentDeviceId, current.uri, resumeAt);
        lastWriteRef.current = { at: Date.now(), positionMs: resumeAt, isPlaying: true };
        await updatePlayback(vid, { isPlaying: true, positionMs: resumeAt, durationMs });
      }
    } catch (err) {
      console.error("❌ PlayPause error:", err);
      setPlayerError(`Couldn't ${isPlaying ? "pause" : "resume"}: ${err.message}`);
    }
  }, [isPlaying, player, durationMs, currentPositionMs]);

  const handleRestart = useCallback(async () => {
    await activatePlayer();
    await restartTrack();
  }, []);

  const handleSkip = useCallback(async () => {
    await activatePlayer();
    await smoothTransition(advance);
  }, [advance, smoothTransition]);

  // After a reload the venue's record can say "playing" while this device has
  // nothing loaded. Once the player is ready, mark it paused so nobody sees a
  // bar moving with no sound; the host can press play to start the song over.
  const reconciledRef = useRef(false);
  useEffect(() => {
    if (!isVenueMode || !playerReady || !player || !venueId) { reconciledRef.current = false; return; }
    if (reconciledRef.current || !nowPlaying) return;
    reconciledRef.current = true;
    setTimeout(async () => {
      try {
        const state = await player.getCurrentState();
        const loaded = state?.track_window?.current_track?.uri === nowPlayingRef.current?.uri;
        if (!loaded && playbackRef.current.isPlaying) {
          const at = currentPositionMs();
          lastWriteRef.current = { at: Date.now(), positionMs: at, isPlaying: false };
          await updatePlayback(venueId, { isPlaying: false, positionMs: at, durationMs: playbackRef.current.durationMs });
        }
      } catch (e) { /* player not ready yet; leave as is */ }
    }, 3000);
    return () => {};
  }, [isVenueMode, playerReady, player, venueId, nowPlaying, currentPositionMs]);

  // Start the next song by itself when the speakers are idle and there is
  // something to play: a request, or the venue's playlist
  const fallbackReady = fallback?.status === "ready";
  useEffect(() => {
    if (!isVenueMode || !playerReady || nowPlaying) return;
    if (queue.length === 0 && !fallbackReady) return;
    if (!activatedRef.current || advancingRef.current || transitioningRef.current) return;
    if (Date.now() < retryBlockedUntilRef.current) return;
    advance();
  }, [isVenueMode, playerReady, nowPlaying, queue, fallbackReady, advance]);

  // A request always beats the venue's playlist: when one arrives during a
  // playlist song, blend out of that song and into the request
  useEffect(() => {
    if (!isVenueMode || !playerReady || !nowPlaying || nowPlaying.source !== "playlist") return;
    if (queue.length === 0) return;
    smoothTransition(advance, { onlyIfQueued: true });
  }, [isVenueMode, playerReady, nowPlaying, queue, advance, smoothTransition]);

  // Enough people voted to skip: the venue's player moves on
  useEffect(() => {
    if (!isVenueMode || !playerReady || !nowPlaying) return;
    if (skipVoters.length >= SKIP_VOTES_NEEDED) smoothTransition(advance);
  }, [isVenueMode, playerReady, nowPlaying, skipVoters, advance, smoothTransition]);

  // ─── The venue's own playlist (what plays when nobody has requested anything) ──
  const loadFallbackPlaylist = useCallback(async (id) => {
    setFallback({ id, name: "", count: 0, status: "loading" });
    try {
      const token = await getValidSpotifyToken();
      if (!token) throw new Error("Spotify isn't connected");
      const info = await getPlaylistInfo(token, id);
      const tracks = await getPlaylistTracks(token, id);
      if (!tracks.length) throw new Error("That playlist has no songs Tapped can play");
      fallbackTracksRef.current = tracks;
      fallbackOrderRef.current = [];
      setFallback({ id, name: info.name, count: tracks.length, status: "ready" });
      try { localStorage.setItem(FALLBACK_KEY, JSON.stringify({ id, name: info.name })); } catch (e) { /* storage unavailable */ }
    } catch (err) {
      fallbackTracksRef.current = [];
      setFallback({ id, name: "", count: 0, status: "error", error: err.message, needsAccess: !!err.needsAccess });
    }
  }, []);

  const setFallbackPlaylist = useCallback(async (input) => {
    const id = parsePlaylistId(input);
    if (!id) {
      setFallback({ id: null, name: "", count: 0, status: "error", error: "That doesn't look like a Spotify playlist link. In Spotify, choose Share → Copy link to playlist." });
      return;
    }
    await loadFallbackPlaylist(id);
  }, [loadFallbackPlaylist]);

  const clearFallbackPlaylist = useCallback(() => {
    try { localStorage.removeItem(FALLBACK_KEY); } catch (e) { /* storage unavailable */ }
    fallbackTracksRef.current = [];
    fallbackOrderRef.current = [];
    setFallback(null);
  }, []);

  // Load the saved playlist when this device starts running a venue
  const fallbackLoadedRef = useRef(false);
  useEffect(() => {
    if (!isVenueMode || !playerReady) { fallbackLoadedRef.current = false; return; }
    if (fallbackLoadedRef.current) return;
    fallbackLoadedRef.current = true;
    try {
      const saved = JSON.parse(localStorage.getItem(FALLBACK_KEY) || "null");
      if (saved?.id) loadFallbackPlaylist(saved.id);
    } catch (e) { /* ignore a corrupted saved value */ }
  }, [isVenueMode, playerReady, loadFallbackPlaylist]);

  // Keep the venue's screen awake so the speakers don't go quiet
  useEffect(() => {
    if (!isVenueMode || !venueId || !navigator.wakeLock) return;
    let lock = null;
    let cancelled = false;
    const request = async () => {
      try {
        lock = await navigator.wakeLock.request("screen");
        if (cancelled) lock.release();
      } catch (e) { /* not available or denied; harmless */ }
    };
    request();
    const onVisible = () => { if (document.visibilityState === "visible") request(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisible);
      if (lock) lock.release().catch(() => {});
    };
  }, [isVenueMode, venueId]);

  const joinVenue = async (id) => { setVenueId(id); };

  const leaveVenue = () => {
    if (venueId && user && !user.isGuest) checkOutOfVenue(venueId, user.uid);
    setVenueId(null);
    setVenue(null);
    setQueue([]);
    setNowPlayingState(null);
    setPlayback({ isPlaying: false, positionMs: 0, durationMs: 0, receivedAt: 0 });
    setSkipVoters([]);
    setIsVenueMode(false);
    setPlayerReady(false);
    setDeviceId(null);
    deviceIdRef.current = null;
    activatedRef.current = false;
  };

  return (
    <VenueContext.Provider value={{
      venueId, venue, queue, nowPlaying, isPlaying, progress, positionMs, durationMs, skipVoters,
      vibeScore, activeUsers, isVenueMode, setIsVenueMode,
      playerReady, playerError, deviceId, player,
      joinVenue, leaveVenue, initPlayer,
      handlePlaySong, handlePlayPause, handleStartMusic, handleRestart, handleSkip,
      fallback, setFallbackPlaylist, clearFallbackPlaylist,
    }}>
      {children}
    </VenueContext.Provider>
  );
}

export function useVenue() {
  const context = useContext(VenueContext);
  if (!context) throw new Error("useVenue must be used within VenueProvider");
  return context;
}
